// @flow

import React, { Component, Fragment } from 'react';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import { Link } from 'react-router-dom';
import { css } from 'react-emotion';
import { get } from 'lodash';
import {
  IconButton,
  Input,
  InputLabel,
  InputAdornment,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { elements, components, fonts, colors } from '@peachjar/components';

import RegistrationFormDisclaimer from '../components/RegistrationFormDisclaimer';
import InlineTip from '../components/InlineTip';
import { PEACHJAR_CONTACT_NUMBER } from '../../../constants';
import sendDistrictMagicLinkEmail from '../utils/sendDistrictMagicLinkEmail';

const { EyeIcon, typography } = elements;
const { Note } = typography;
const { ProximaNova } = fonts;
const { dragon, slate, jungle, platinum, soil, blonde } = colors;
const { notifyError } = components.Notifications;
const { ButtonPrimaryLarge } = components.Buttons;
const { Spinner } = components.LoadingIndicators;

const styles = {
  validator: css`
    padding: 1rem;
    border: 1px solid ${soil};
    border-radius: 3px;
    margin-left: -5px;
    margin-top: 2.5rem;
    background-color: ${blonde};
    display: flex;
    flex-direction: column;
  `,
  marginVertical: css`
    margin-top: 10px;
    margin-bottom: 10px;
  `,
  inputPassword: css({
    maxWidth: '420px',
    fontFamily: ProximaNova,
    marginTop: '32px',
  }),
  inputEmail: css({
    maxWidth: '420px',
    fontFamily: ProximaNova,
    marginTop: '2rem',
  }),
  buttonWrapper: css({
    display: 'flex',
    marginLeft: '-5px',
    marginTop: '3rem',
  }),
  disclaimer: {
    maxWidth: '420px',
    fontFamily: ProximaNova,
    fontSize: '13px',
    color: slate,
    marginTop: '2rem',
    textAlign: 'left',
  },
  tempLink: css({
    color: jungle,
    textDecoration: 'none',
  }),
  circle: {
    width: 8,
    height: 8,
    borderRadius: 8 / 2,
    padding: 2,
    marginLeft: 4,
    marginRight: 8,
  },
  itemThing: {
    alignItems: 'center',
    margin: 6,
    color: slate,
    fontSize: '13px',
  },
};

// TODO qktoday don't define all this stuff here :) and refactor
const MIN_PASSWORD_LENGTH = 8;
const passesRegex = regex => pw => regex.test(pw);
const hasLowerRegex = /(?=.*[a-z])/;
const hasUpperRegex = /(?=.*[A-Z])/;
const hasDigitRegex = /(?=.*\d)/;
const isProbablyValidEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const meetsLengthReq = pw => pw.length >= MIN_PASSWORD_LENGTH;
const checkHasLower = passesRegex(hasLowerRegex);
const checkHasUpper = passesRegex(hasUpperRegex);
const checkHasDigit = passesRegex(hasDigitRegex);
const checkIfProbablyValidEmail = passesRegex(isProbablyValidEmailRegex);

const Circle = ({ color }) => (
  <div style={{ ...styles.circle, backgroundColor: color }} />
);

const PWReq = ({ meetsReq, label, angry }) => {
  const errorColor = angry ? 'gray' : dragon;
  const circleColor = meetsReq ? jungle : errorColor;
  return (
    <div className="row" style={styles.itemThing}>
      <Circle color={circleColor} />
      {label}
    </div>
  );
};

// FIXME remove
const emailValidationMessage = value =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? 'Invalid email address'
    : undefined;
const isPasswordOK = pw => {
  const meetsRequirements =
    pw &&
    meetsLengthReq(pw) &&
    checkHasUpper(pw) &&
    checkHasLower(pw) &&
    checkHasDigit(pw);
  return pw && !meetsRequirements ? 'Missing Requirement' : undefined;
};

const renderTextField = ({
  input,
  label,
  meta: { touched, error, active, dirty, asyncValidating },
  id,
  name,
  endAdornment,
  type,
  ...custom
}): React.DOM<*> => {
  const { value } = input;
  const isErrorDisplayed = touched && !active && error;
  return (
    <div className={styles.inputPassword}>
      <FormControl fullWidth error={isErrorDisplayed} {...input} {...custom}>
        <InputLabel htmlFor="name-error">{label}</InputLabel>
        <Input
          id="name-error"
          value={value}
          endAdornment={endAdornment}
          type={type}
          inputProps={{
            'data-testid': `field-${name}`,
          }}
        />
        {isErrorDisplayed && (
          <FormHelperText id="name-error-text">{error}</FormHelperText>
        )}
      </FormControl>
    </div>
  );
};

const ACCOUNT_CREATION_MUTATION = gql`
  mutation createSchoolOrDistrictUser(
    $userInfo: SchoolOrDistrictUserRegistrationInfo
  ) {
    createSchoolOrDistrictUser(userInfo: $userInfo) {
      id
    }
  }
`;

type Props = {|
  +formValues: {
    email?: string,
    password?: string,
    numberOfSchools?: string,
  },
  +registrationId: ?string,
  +nextPage: () => void,
  +invalid: boolean,
  +submitting: boolean,
  +pristine: boolean,
  +hasPaidAndSigned: boolean,
  +onNotifyError: () => void
|};

type State = {|
  isPasswordFieldFocused: boolean,
  showPassword: boolean,
|};

class RegistrationForm extends Component<Props, State> {
  state = {
    isPasswordFieldFocused: false,
    showPassword: false,
  };

  onTogglePasswordFieldFocus = () => {
    const { isPasswordFieldFocused } = this.state;
    this.setState({ isPasswordFieldFocused: !isPasswordFieldFocused });
  };

  handleMouseDownPassword = event => {
    event.preventDefault();
  };

  handleClickShowPassword = () => {
    this.setState({ showPassword: !this.state.showPassword });
  };

  handleUserInfoSubmission = (createUserAccount, mutationVariables) => () => {
    createUserAccount({
      variables: mutationVariables,
    });
  };

  handleCompletedMutation = (nextPage, hasPaidAndSigned) => () => {
    sendDistrictMagicLinkEmail(this.props.formValues.email, hasPaidAndSigned);
    nextPage();
  };

  handleErroredMutation = error => {
    const message = get(error, 'graphQLErrors[0].message');
    this.props.onNotifyError(message);
  };

  render() {
    const {
      formValues,
      registrationId,
      nextPage,
      invalid,
      submitting,
      pristine,
      hasPaidAndSigned,
      onNotifyError,
    } = this.props;
    const { email, password, numberOfSchools } = formValues;
    const mutationVariables = {
      userInfo: { email, password, registrationId },
    };

    const { isPasswordFieldFocused, showPassword } = this.state;

    const { email: emailValue = '', password: passwordValue = '' } = formValues;
    const meetsMinLength = meetsLengthReq(passwordValue);
    const hasLower = checkHasLower(passwordValue);
    const hasUpper = checkHasUpper(passwordValue);
    const hasDigit = checkHasDigit(passwordValue);
    const isPasswordValid = meetsMinLength && hasLower && hasUpper && hasDigit;
    const isProbablyValidEmail =
      emailValue.length > 0 && checkIfProbablyValidEmail(emailValue);
    const isSubmitEnabled =
      isProbablyValidEmail &&
      isPasswordValid &&
      !invalid &&
      !submitting &&
      !pristine;
    const org = Number(numberOfSchools) > 1 ? 'District' : 'School';

    return (
      <div
        className={css({
          width: '70%',
        })}
      >
        <h1>{`Next, let's create your ${org.toLowerCase()}'s account.`}</h1>
        <div>
          <form>
            <Field
              id="email"
              name="email"
              label={`${org} Contact Email Address`}
              type="email"
              className={styles.inputEmail}
              component={renderTextField}
              validate={[emailValidationMessage]}
              warn={[emailValidationMessage]}
              fullWidth
            />
            <Field
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              onFocus={this.onTogglePasswordFieldFocus}
              onBlur={this.onTogglePasswordFieldFocus}
              className={styles.inputPassword}
              component={renderTextField}
              validate={[isPasswordOK]}
              fullWidth
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={this.handleClickShowPassword}
                    onMouseDown={this.handleMouseDownPassword}
                    tabindex="-1"
                  >
                    <EyeIcon isPasswordVisible={this.state.showPassword} />
                  </IconButton>
                </InputAdornment>
              }
            />
            {isPasswordFieldFocused || passwordValue.length ? (
              <InlineTip id="password-validation">
                <div className={`col-md-6 ${styles.marginVertical}`}>
                  <PWReq
                    meetsReq={meetsMinLength}
                    angry={isPasswordFieldFocused}
                    label="8 character minimum"
                  />
                  <PWReq
                    meetsReq={hasDigit}
                    angry={isPasswordFieldFocused}
                    label="1 number"
                  />
                </div>
                <div className={`col-md-6 ${styles.marginVertical}`}>
                  <PWReq
                    meetsReq={hasLower}
                    angry={isPasswordFieldFocused}
                    label="1 lowercase letter"
                  />
                  <PWReq
                    meetsReq={hasUpper}
                    angry={isPasswordFieldFocused}
                    label="1 uppercase letter"
                  />
                </div>
              </InlineTip>
            ) : null}

            {!registrationId && (
              <div className={styles.validator}>
                <Note>
                  <b>Note</b>: A registration is not associated with your
                  session. Please return to the{' '}
                  <Link className={styles.tempLink} to="/onboarding">
                    pricing page
                  </Link>{' '}
                  and try again or call us at {PEACHJAR_CONTACT_NUMBER} for
                  assistance.
                </Note>
              </div>
            )}

            <Mutation
              mutation={ACCOUNT_CREATION_MUTATION}
              onError={this.handleErroredMutation}
              onCompleted={this.handleCompletedMutation(
                nextPage,
                hasPaidAndSigned
              )}
            >
              {(createUserAccount, { data, loading, error }) => (
                <Fragment>
                  <div className={styles.buttonWrapper}>
                    <ButtonPrimaryLarge
                      disabled={
                        (!isSubmitEnabled && !loading) || !registrationId
                      }
                      data-testid="submit-createAccount"
                      onClick={this.handleUserInfoSubmission(
                        createUserAccount,
                        mutationVariables
                      )}
                    >
                      {loading && <Spinner />}
                      Create Account
                    </ButtonPrimaryLarge>
                  </div>
                </Fragment>
              )}
            </Mutation>
          </form>
          <RegistrationFormDisclaimer styles={styles.disclaimer} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const form = state.form.onboarding;
  const values = form && form.values;
  const onboarding = state.onboarding;

  return {
    formValues: { ...values, ...onboarding },
  }; // FIXME create selector, also, don't need formValues here, this is just for the alert example
};

const mapDispatchToProps = dispatch => ({
  onNotifyError: message => dispatch(notifyError(message)),
});

RegistrationForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegistrationForm);

export default reduxForm({
  form: 'onboarding', //             <------ same form name
  destroyOnUnmount: false, //        <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  // validate,
})(RegistrationForm);



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/UserRegistration/containers/UserRegistrationForm.jsx